import React, { useCallback, useEffect, useState } from 'react';
import {
  Eye, RefreshCw, Cpu, Database, Server, GitBranch, Activity,
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { parsePrometheusText, sumMetric, groupMetric } from '../utils/prometheus';
import { formatNumber, formatCompactNumber, formatDateTime } from '../utils/format';
import './AdminPage.css';

const SystemHealth: React.FC = () => {
  const [version, setVersion] = useState<any | null>(null);
  const [health, setHealth] = useState<any | null>(null);
  const [redisStatus, setRedisStatus] = useState<any | null>(null);
  const [mediaStatus, setMediaStatus] = useState<any | null>(null);
  const [metricsText, setMetricsText] = useState<string>('');
  const [metricsErr, setMetricsErr] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    const [v, h, r, m, p] = await Promise.allSettled([
      adminService.getVersion(),
      adminService.healthCheck(),
      adminService.getRedisStatus(),
      adminService.getMediaStatus(),
      adminService.getPrometheusText(),
    ]);
    if (v.status === 'fulfilled') setVersion(v.value);
    if (h.status === 'fulfilled') setHealth(h.value);
    if (r.status === 'fulfilled') setRedisStatus(r.value);
    if (m.status === 'fulfilled') setMediaStatus(m.value);
    if (p.status === 'fulfilled') {
      setMetricsText(p.value || '');
      setMetricsErr('');
    } else {
      setMetricsErr((p.reason as any)?.response?.data?.detail || 'Metrics unavailable');
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const samples = parsePrometheusText(metricsText);
  const requestTotal = sumMetric(samples, 'http_requests_total');
  const errorTotal =
    sumMetric(samples, 'http_requests_total', { status_code: '500' })
    + sumMetric(samples, 'http_requests_total', { status: '500' });
  const inFlight = sumMetric(samples, 'http_requests_in_progress');
  const byEndpoint = groupMetric(samples, 'http_requests_total', 'endpoint');
  const topEndpoints = Object.entries(byEndpoint)
    .map(([endpoint, total]) => ({ endpoint, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Eye size={20} style={{ marginRight: 8 }} />System Health</h1>
          <p className="page-subtitle">Live infra check, Prometheus snapshot and version info</p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Version</span>
          <span className="kpi-value">{version?.version || '—'}</span>
          {version?.deploy_id && <span className="kpi-detail">{version.deploy_id}</span>}
        </div>
        <div className="kpi-card">
          <span className="kpi-label">API Status</span>
          <span className="kpi-value" style={{
            color: health?.status === 'healthy' ? '#16a34a' : health?.status === 'degraded' ? '#f59e0b' : '#dc2626',
          }}>{health?.status || 'unknown'}</span>
          {health?.timestamp && <span className="kpi-detail">@ {formatDateTime(health.timestamp * 1000)}</span>}
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Database</span>
          <span className="kpi-value">{health?.services?.database || '—'}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Redis</span>
          <span className="kpi-value">{health?.services?.redis || redisStatus?.status || '—'}</span>
        </div>
      </div>

      <div className="charts-row-2">
        <div className="panel">
          <div className="panel-header">
            <h3><Activity size={16} /> Request volume</h3>
          </div>
          <div className="panel-body">
            <div className="kpi-grid" style={{ marginBottom: 0 }}>
              <div className="kpi-card">
                <span className="kpi-label">Total requests</span>
                <span className="kpi-value">{formatCompactNumber(requestTotal)}</span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">5xx responses</span>
                <span className="kpi-value" style={{ color: errorTotal > 0 ? '#dc2626' : '#16a34a' }}>
                  {formatCompactNumber(errorTotal)}
                </span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">In-flight</span>
                <span className="kpi-value">{formatCompactNumber(inFlight)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="panel">
          <div className="panel-header">
            <h3><GitBranch size={16} /> Features</h3>
          </div>
          <div className="panel-body">
            {version?.features?.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {version.features.map((f: string) => (
                  <span key={f} className="pill healthy">{f}</span>
                ))}
              </div>
            ) : <div className="empty">No feature flags reported</div>}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3><Cpu size={16} /> Top endpoints by request count</h3>
          {metricsErr && <span className="pill warning">{metricsErr}</span>}
        </div>
        <div className="panel-body tight">
          {loading ? <div className="skeleton" style={{ height: 200, margin: 16 }} /> : topEndpoints.length === 0 ? (
            <div className="empty">No HTTP request metrics emitted yet</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Endpoint</th><th>Requests</th></tr>
              </thead>
              <tbody>
                {topEndpoints.map((r) => (
                  <tr key={r.endpoint}>
                    <td><code>{r.endpoint}</code></td>
                    <td>{formatNumber(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="charts-row-2">
        <div className="panel">
          <div className="panel-header">
            <h3><Database size={16} /> Redis</h3>
          </div>
          <div className="panel-body">
            <pre style={{ background: '#f8fafc', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12 }}>
              {redisStatus ? JSON.stringify(redisStatus, null, 2) : '—'}
            </pre>
          </div>
        </div>
        <div className="panel">
          <div className="panel-header">
            <h3><Server size={16} /> Media (R2)</h3>
          </div>
          <div className="panel-body">
            <pre style={{ background: '#f8fafc', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12 }}>
              {mediaStatus ? JSON.stringify(mediaStatus, null, 2) : '—'}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealth;
